import { TextLayer } from "@deck.gl/layers"
import { pointInPolygon } from "@nova/calculate-geospatial"
import { AreaUnit, DistanceUnit, GeometryType } from "@nova/constants"
import { LngLatCoordinatePair } from "features/map/event/types"
import { LngLat } from "maplibre-gl"
import store from "integrations/redux/store"
import { Midpoint } from "plugins/shared/layers/GeometryLayer.class"
import { PolygonGeometry } from "plugins/shared/layers/GeometryLinesLayer.class"
import { formatNumberWithCommas } from "@nova/util-strings"
import { RootState } from "integrations/redux/rootReducer"
import { ibmPlexSans } from "configuration/fonts"
import { selectRulersWithMockToDraw } from "features/entity-caching/rulers/selectors"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectRulerTemplate } from "plugins/Toolbox/tool-registry"
import { getViewport } from "features/map/GlobalViewState"
import { geometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"
import { convertDistance } from "@nova/format"

export const formatMap: { area: Record<AreaUnit, string>; distance: Record<DistanceUnit, string> } =
    {
        area: {
            [AreaUnit.SquareFeet]: "ft²",
            [AreaUnit.SquareMeters]: "m²",
            [AreaUnit.SquareMiles]: "mi²",
            [AreaUnit.SquareKilometers]: "km²",
            [AreaUnit.Acres]: "ac",
            [AreaUnit.Hectares]: "ha",
        },
        distance: {
            [DistanceUnit.Feet]: "ft",
            [DistanceUnit.Meters]: "m",
            [DistanceUnit.Yards]: "yd",
            [DistanceUnit.Kilometers]: "km",
            [DistanceUnit.Miles]: "mi",
            [DistanceUnit.NauticalMiles]: "NM",
        },
    }

type TextMidpoint = Midpoint & { idx: number }

function getAngleBetweenPoints(a: LngLatCoordinatePair, b: LngLatCoordinatePair) {
    const angleRadians = Math.atan2(b[0] - a[0], b[1] - a[1])
    let angleDegrees = angleRadians * (180 / Math.PI)

    if (angleDegrees > 0) angleDegrees += 180

    return angleDegrees + 90
}
const getMidpoints = (
    polygon: PolygonGeometry,
    isLine: boolean,
    current: boolean,
    thinLinePoint?: LngLatCoordinatePair
) => {
    const points = [...polygon.coordinates]
    if (!points || points.length === 0) return []
    if (!isLine) points.push(points[0])
    if (current && thinLinePoint)
        points.push({ longitude: thinLinePoint[0], latitude: thinLinePoint[1] })
    const midpoints = points.slice(0, -1).map((p, i) => ({
        midpoint: [
            (p.longitude + points[i + 1].longitude) / 2,
            (p.latitude + points[i + 1].latitude) / 2,
        ] as LngLatCoordinatePair,
        first: [p.longitude, p.latitude] as LngLatCoordinatePair,
        second: [points[i + 1].longitude, points[i + 1].latitude] as LngLatCoordinatePair,
        idx: polygon.deckGlIdx,
    }))
    return midpoints
}

export const RulerMidpointTextLayer = (
    prefix: string,
    data: PolygonGeometry[],
    thinLinePoint?: LngLatCoordinatePair
) => {
    const state = store.getState() as RootState
    const workspaceDistanceUnit = selectWorkspaceDistanceUnit(state)
    const rulerTemplate = selectRulerTemplate(state)
    const rulers = selectRulersWithMockToDraw(state)
    const current = rulers.length - 1
    const midpoints = data.reduce((acc, cur) => {
        acc.push(
            ...getMidpoints(
                cur,
                cur.geometryType === GeometryType.Line,
                cur.id === geometryDataStore.polygon.id,
                thinLinePoint
            )
        )
        return acc
    }, [] as TextMidpoint[])
    const view = getViewport()
    const defaultCharacters = ["²"]
    for (let i = 32; i <= 128; i++) {
        defaultCharacters.push(String.fromCharCode(i))
    }
    return new TextLayer({
        id: `${prefix}-text-ruler`,
        data: midpoints,
        getPosition: (d: TextMidpoint) => d.midpoint,
        getText: (d: TextMidpoint) => {
            const left = new LngLat(d.first[0], d.first[1])
            const right = new LngLat(d.second[0], d.second[1])
            const dist = left.distanceTo(right)
            const ruler = rulers[d.idx] as { distanceUnit?: DistanceUnit }
            const distanceUnit =
                ruler.distanceUnit || rulerTemplate.distanceUnit || workspaceDistanceUnit
            const converted = convertDistance(dist, DistanceUnit.Meters, distanceUnit)
            return formatNumberWithCommas(converted) + " " + formatMap.distance[distanceUnit]
        },
        fontWeight: 500,
        getAlignmentBaseline: "center",
        getColor: [255, 255, 255],
        getBackgroundColor: [12, 12, 15, 243], // black-900
        backgroundBorderRadius: 6,
        getSize: 10,
        getTextAnchor: "middle",
        background: true,
        extensions: [...getGlobalTerrainData().extensions],
        backgroundPadding: [8, 4],
        fontFamily: ibmPlexSans.style.fontFamily,
        characterSet: defaultCharacters,
        getAngle: (d: TextMidpoint) => {
            return getAngleBetweenPoints(
                view.project(d.first) as LngLatCoordinatePair,
                view.project(d.second) as LngLatCoordinatePair
            )
        },
        getPixelOffset: (d: TextMidpoint) => {
            const angle = getAngleBetweenPoints(
                view.project(d.first) as LngLatCoordinatePair,
                view.project(d.second) as LngLatCoordinatePair
            )
            const defaultAngle = [
                17 * Math.sin((Math.abs((angle + 360) % 360) * Math.PI) / 180),
                17 * Math.cos((Math.abs((angle + 360) % 360) * Math.PI) / 180),
            ] as LngLatCoordinatePair
            if (midpoints.length < 2) return defaultAngle
            const polygon = data.find((e) => e.deckGlIdx === d.idx)
            if (!polygon) return defaultAngle
            const coordinates = [...polygon.coordinates]
            if (polygon?.deckGlIdx === current && thinLinePoint)
                coordinates.push({ longitude: thinLinePoint[0], latitude: thinLinePoint[1] })
            if (
                pointInPolygon(
                    {
                        longitude: d.midpoint[0] + defaultAngle[0] * 0.00000001,
                        latitude: d.midpoint[1] + defaultAngle[1] * -0.00000001,
                    },
                    coordinates
                )
            )
                return [
                    17 * Math.sin((Math.abs((angle + 540) % 360) * Math.PI) / 180),
                    17 * Math.cos((Math.abs((angle + 540) % 360) * Math.PI) / 180),
                ]
            else return defaultAngle
        },
        parameters: { depthTest: false },
    })
}
